import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "./App.css";
import NavigationHeader from "./components/NavigationHeader";
import Footer from "./components/Footer";
import AuthCallback from "./components/AuthCallback";
import { AuthProvider } from "./context/AuthContext";
import Home from "./pages/Home";
import RemoteViewing from "./pages/RemoteViewing";
import AstralProjection from "./pages/AstralProjection";
import Guidebooks from "./pages/Guidebooks";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Shop from "./pages/Shop";
import Account from "./pages/Account";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error(error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#07080B] px-6 text-center">
          <div>
            <p className="eyebrow">Signal Lost</p>
            <p className="font-display mt-4 text-2xl text-[#F0F2F8]">
              Something drifted out of alignment.
            </p>
            <button
              data-testid="error-boundary-reset-btn"
              onClick={() => {
                this.setState({ hasError: false });
                window.location.href = "/";
              }}
              className="mt-8 border border-[#8B5CF6]/50 px-6 py-3 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8]"
            >
              Return to base
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  const raf = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    const loop = (t) => {
      lenis.raf(t);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf.current);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Process the OAuth session_id synchronously during render, before any
  // auth state check runs — prevents race conditions on return from Google.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/remote-viewing" element={<RemoteViewing />} />
      <Route path="/astral-projection" element={<AstralProjection />} />
      <Route path="/guidebooks" element={<Guidebooks />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/account" element={<Account />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <SmoothScroll />
          <ScrollManager />
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:border focus:border-[#8B5CF6] focus:bg-[#07080B] focus:px-4 focus:py-2 focus:text-sm focus:text-[#F0F2F8]"
          >
            Skip to content
          </a>
          <div className="grain-overlay" aria-hidden="true" />
          <NavigationHeader />
          <main id="main">
            <AppRoutes />
          </main>
          <Footer />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
